// Long-form, unique body content for the SEO category (theme) pages.
// CommonJS so both the React app (ThemeLanding) and the Node prerender script
// can consume it. No fake listings, reviews, prices, or claims.
const SECTIONS = {
  "yoga-retreats": [
    {
      h2: "What actually happens on a yoga retreat",
      paras: [
        "Most yoga retreats follow a gentle daily rhythm: an energising morning practice, a nourishing breakfast, free time to rest or explore, and a slower restorative session in the late afternoon. Around that structure you'll usually find breathwork, meditation and plenty of unhurried space to switch off from everyday life.",
        "The teaching style varies widely. Some retreats lean into dynamic vinyasa and ashtanga for practitioners who want to sweat and progress, while others centre on yin, hatha and meditation for people who mainly want to slow down. Reading the daily schedule before you book is the easiest way to tell which end of the spectrum a retreat sits on.",
      ],
    },
    {
      h2: "How to choose the right yoga retreat for you",
      paras: [
        "Start with your intention. If you want to deepen a regular practice, look for retreats that list the styles and teachers explicitly and offer more than one class a day. If you mostly want to rest, prioritise a calmer location, smaller group sizes and generous free time over a packed timetable.",
        "Then weigh the practicalities: travel time to the venue, whether meals and airport transfers are included, the group size, and whether the retreat is mixed-level or aimed at a specific ability. A short message to the organiser answering these questions usually tells you everything you need before committing.",
      ],
    },
    {
      h2: "Best time of year to go",
      paras: [
        "Southern-European yoga retreats run comfortably from spring through autumn, with the shoulder seasons of May–June and September–October offering warm weather without peak-summer crowds or prices. Island and coastal venues are busiest in July and August.",
        "If you're chasing quiet and cooler mornings for practice, the shoulder seasons are ideal. For guaranteed sunshine and swimming between sessions, mid-summer is the safer bet — just book earlier, as the best venues fill quickly.",
      ],
    },
  ],
  "wellness-retreats": [
    {
      h2: "What a wellness retreat includes",
      paras: [
        "A wellness retreat is built around recovery rather than a fixed practice. Expect a mix of spa treatments, thermal or sea bathing, gentle movement such as walking or stretching, and seasonal food designed to leave you feeling lighter. The goal is to genuinely rest, not to keep to a demanding schedule.",
        "Because 'wellness' covers a lot of ground, offerings differ. Some venues focus on medical or detox programmes, others on thermal bathing culture, and others simply on beautiful spaces where you can do very little. Check what's included versus what costs extra so there are no surprises on arrival.",
      ],
    },
    {
      h2: "Who wellness retreats suit best",
      paras: [
        "Wellness retreats are a good fit if you're feeling depleted and want permission to stop. They work well for solo travellers, couples and small groups of friends who'd rather share a spa and long lunches than follow a class timetable.",
        "They're also forgiving for anyone who isn't especially fit or flexible: activities are optional and gentle, so you can do as much or as little as you like without feeling you're missing out.",
      ],
    },
    {
      h2: "Getting the most from your reset",
      paras: [
        "Arrive with a light plan rather than a full itinerary. Booking one or two anchor treatments and leaving the rest of the day open tends to feel more restorative than trying to fit everything in.",
        "Small habits help the reset stick: stepping away from your phone, keeping a consistent sleep and meal rhythm, and spending time outdoors. The point is to return home with a routine you can actually maintain, not just a pleasant memory.",
      ],
    },
  ],
  "yacht-retreats": [
    {
      h2: "Life on board a yacht retreat",
      paras: [
        "A yacht retreat swaps the hotel for the water. Days typically start with a slow breakfast at anchor, followed by a few hours of sailing to the next bay, with stops for swimming, snorkelling and lunch on deck. Evenings are spent in a quiet harbour or moored under the stars.",
        "Most trips are crewed or skippered, so you don't need any sailing knowledge — you're free to relax while the crew handles the route and the ropes. Cabins are compact by nature, which is part of the appeal: less stuff, more sea.",
      ],
    },
    {
      h2: "Choosing a route and a boat",
      paras: [
        "Regions matter. The Greek islands and the Croatian coast are popular for calm, sheltered sailing and short hops between islands, while the western Mediterranean offers a livelier, more glamorous scene. Pick the vibe first, then the specific route.",
        "Consider the group setup too: a private charter for your own group feels very different from a cabin-charter where you share the boat with other travellers. Both are great — one is sociable, the other is intimate.",
      ],
    },
    {
      h2: "When to sail and what to pack",
      paras: [
        "The Mediterranean sailing season runs roughly May to October, with the warmest water and longest days in high summer. Shoulder-season sailing is cooler but quieter and often better value.",
        "Pack light and soft: a couple of swimsuits, layers for breezy evenings, reef-safe sunscreen, a hat and soft-soled shoes. Hard suitcases don't stow well, so a duffel or soft bag is your friend on board.",
      ],
    },
  ],
  "yoga-sailing-retreats": [
    {
      h2: "How yoga and sailing combine",
      paras: [
        "A yoga sailing retreat weaves a daily practice into a sailing holiday. Mornings usually open with yoga on deck or on a quiet beach as the light comes up, followed by a day of sailing, swimming and exploring sheltered coves before an optional evening wind-down session.",
        "The pace is deliberately gentle. Because the boat anchors in calm bays for practice, sessions tend to be accessible and restorative rather than intense — the sea does a lot of the relaxing for you.",
      ],
    },
    {
      h2: "Who these trips are for",
      paras: [
        "Yoga sailing retreats suit people who love the water as much as the mat, as well as couples and friends looking for something a little different from a standard retreat. No sailing experience is needed on crewed trips.",
        "They're also a good middle ground if one half of a pair is more into yoga and the other more into the sea — there's genuinely something for both on the same trip.",
      ],
    },
    {
      h2: "Practical tips before you book",
      paras: [
        "Ask how much practice is scheduled and where it happens — on deck, on shore, or both — so the balance of yoga and sailing matches what you want. Group size and cabin arrangements are worth confirming too.",
        "If you're prone to seasickness, choose calmer regions and shorter crossings, and bring your usual remedies. Most itineraries deliberately favour sheltered waters, which keeps both the sailing and the yoga comfortable.",
      ],
    },
  ],
  "retreats-for-beginners": [
    {
      h2: "Why beginner-friendly retreats are different",
      paras: [
        "Beginner-friendly retreats are designed to remove the intimidation factor. Classes are taught for all levels with plenty of guidance and modifications, groups are welcoming, and schedules leave room to breathe rather than packing every hour.",
        "You'll rarely be the only first-timer. These retreats attract lots of people trying it for the first time, which makes the atmosphere relaxed and easy to settle into — nobody expects you to know the poses or the jargon.",
      ],
    },
    {
      h2: "What to expect as a first-timer",
      paras: [
        "Expect clear instructions, optional participation, and teachers who check in with you. You can always rest in a pose, skip a session, or ask questions — a good retreat makes that easy and normal.",
        "Meals are usually communal and relaxed, which is one of the nicest parts: it's an easy way to meet people, especially if you're travelling solo.",
      ],
    },
    {
      h2: "How to pick your first retreat",
      paras: [
        "Look for the words 'all levels' or 'beginner-friendly', a manageable location that's easy to reach, and a schedule with genuine downtime. Smaller groups often feel more supportive for a first experience.",
        "Don't overthink the style — a gentle hatha or yin-focused retreat is a forgiving starting point. Once you've done one, you'll know exactly what you want more (or less) of next time.",
        "It also helps to be honest with the organiser about any injuries, worries or dietary needs before you arrive. A good retreat will happily adapt, and asking early means you can relax into the experience instead of managing surprises once you're there.",
      ],
    },
  ],
};

module.exports = { SECTIONS };
